import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api";
import type {
  NotificationCategory,
  NotificationPreference,
} from "../../types/contract";
import { DialogHeader, Popup } from "../../ui/Popup";

const SECURITY_NOTE = "Password and account security emails are always sent.";

const LABELS: Record<NotificationCategory, string> = {
  security: "Security emails",
  transactional: "Work order and file emails",
  reports: "Scheduled reports",
};

const TOGGLE_ORDER: NotificationCategory[] = ["transactional", "reports"];

function pill(on: boolean): string {
  return `rounded px-1.5 text-[10px] font-medium ${
    on ? "bg-accent/20 text-accent" : "bg-hover text-faint"
  }`;
}

function SwitchRow({
  label,
  checked,
  locked,
  disabled,
  describedBy,
  onToggle,
}: {
  label: string;
  checked: boolean;
  locked: boolean;
  disabled: boolean;
  describedBy?: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      aria-disabled={locked || disabled}
      aria-describedby={describedBy}
      disabled={locked || disabled}
      className="flex w-full items-center gap-2 text-left text-xs text-fg disabled:cursor-default"
      onClick={() => {
        if (locked || disabled) return;
        onToggle();
      }}
    >
      <span className="min-w-0 flex-1">{label}</span>
      <span className={pill(checked)}>{checked ? "On" : "Off"}</span>
    </button>
  );
}

function useNotificationPreferences() {
  const [prefs, setPrefs] = useState<NotificationPreference[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void api.notificationPreferences().then(
      (rows) => {
        if (!cancelled) setPrefs(rows);
      },
      () => {
        if (!cancelled) setLoadError("Could not load notification settings.");
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggle(category: NotificationCategory, enabled: boolean) {
    if (!prefs || savingRef.current || category === "security") return;
    const previous = prefs;
    savingRef.current = true;
    setPrefs(
      prefs.map((row) =>
        row.category === category ? { ...row, enabled } : row,
      ),
    );
    setSaving(true);
    setSaveError(null);
    try {
      setPrefs(
        await api.updateNotificationPreferences([{ category, enabled }]),
      );
    } catch (err) {
      setPrefs(previous);
      setSaveError(
        err instanceof ApiError
          ? err.message
          : "Could not save notification settings.",
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  const toggles = TOGGLE_ORDER.flatMap((category) => {
    const row = prefs?.find((item) => item.category === category);
    return row ? [row] : [];
  });

  return {
    toggles,
    loaded: prefs !== null,
    loadError,
    saveError,
    saving,
    toggle,
  };
}

function NotificationRows({
  toggles,
  loaded,
  loadError,
  saveError,
  saving,
  onToggle,
}: {
  toggles: NotificationPreference[];
  loaded: boolean;
  loadError: string | null;
  saveError: string | null;
  saving: boolean;
  onToggle: (category: NotificationCategory, enabled: boolean) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <SwitchRow
          label={LABELS.security}
          checked
          locked
          disabled={false}
          describedBy="security-email-note"
          onToggle={() => undefined}
        />
        <p id="security-email-note" className="mt-1 text-[11px] text-muted">
          {SECURITY_NOTE}
        </p>
      </div>
      {loadError && (
        <p role="alert" className="text-[11px] text-red-400">
          {loadError}
        </p>
      )}
      {!loaded && !loadError && (
        <p role="status" className="text-[11px] text-faint">
          Loading notification settings.
        </p>
      )}
      {toggles.map((row) => (
        <SwitchRow
          key={row.category}
          label={LABELS[row.category]}
          checked={row.enabled}
          locked={row.locked}
          disabled={saving}
          onToggle={() => onToggle(row.category, !row.enabled)}
        />
      ))}
      {saveError && (
        <p role="alert" className="text-[11px] text-red-400">
          {saveError}
        </p>
      )}
    </div>
  );
}

export function NotificationSettings({ onClose }: { onClose: () => void }) {
  const state = useNotificationPreferences();
  return (
    <Popup
      onClose={onClose}
      className="flex max-h-[85vh] w-full max-w-sm flex-col rounded-lg bg-surface text-fg shadow-2xl"
    >
      <DialogHeader title="Settings" onClose={onClose} />
      <div className="space-y-3 p-4">
        <h3 className="text-xs font-medium text-fg">Notifications</h3>
        <NotificationRows
          toggles={state.toggles}
          loaded={state.loaded}
          loadError={state.loadError}
          saveError={state.saveError}
          saving={state.saving}
          onToggle={(category, enabled) => void state.toggle(category, enabled)}
        />
      </div>
    </Popup>
  );
}
